/**
 * Palletlevering: gewenste leverdatum in de cart drawer.
 * Slaat de datum op als cart attribute "Gewenste leverdatum" (niet als line
 * item property, want geldt voor de hele bestelling) en zet dezelfde datum
 * ook als losse regel in de cart note, zonder een bestaande notitie te
 * overschrijven. Geïsoleerd bestand: raakt geen andere cart-logica.
 *
 * Leverregels vervoersmaatschappij (bijgewerkt, met 1 extra werkdag speling):
 * - Er moet minimaal 2 volledige werkdagen tussen de besteldag en de
 *   leverdag zitten (bv. bestelling op maandag -> dinsdag en woensdag
 *   zijn de bufferdagen -> donderdag is de eerst mogelijke leverdag).
 * - Er wordt nooit op maandag of in het weekend per pallet geleverd.
 *   Valt de berekende datum op maandag, dan schuift de eerst
 *   mogelijke leverdatum door naar dinsdag.
 * - Verder in de toekomst is elke dinsdag/woensdag/donderdag/vrijdag
 *   gewoon te kiezen; er is geen maximumdatum.
 */
(function () {
  if (window.palletDeliveryLoaded) return;
  window.palletDeliveryLoaded = true;

  var DATE_ID = 'PalletDelivery-Date';
  var ERROR_ID = 'PalletDelivery-DateError';
  var FORM_ID = 'CartDrawer-Form';
  var NOTE_TEXTAREA_ID = 'CartDrawer-Note';
  var NOTE_PREFIX = 'Voorkeursdatum palletlevering: ';

  // Minimaal aantal werkdagen tussen besteldag en leverdag (buffer voor de
  // vervoersmaatschappij, incl. 1 extra werkdag speling). 3 werkdagen
  // verder tellen vanaf de besteldag betekent: dag+1 en dag+2 zijn de
  // bufferdagen, dag+3 is de eerst mogelijke leverdag.
  var MIN_LEAD_BUSINESS_DAYS = 3;

  var WEEKDAYS_NL = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  var MONTHS_NL = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december',
  ];

  var saveInFlight = null;
  // Alle opslag-verzoeken lopen door deze keten heen, zodat twee snel na
  // elkaar gekozen datums elkaar niet kunnen inhalen.
  var saveQueue = Promise.resolve();

  function startOfDay(date) {
    var d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function addDays(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function isWeekend(date) {
    var day = date.getDay();
    return day === 0 || day === 6;
  }

  function isMonday(date) {
    return date.getDay() === 1;
  }

  // Palletlevering is nooit op maandag of in het weekend.
  function isDeliveryDayAllowed(date) {
    return !isWeekend(date) && !isMonday(date);
  }

  // Telt "days" werkdagen (ma t/m vr) vooruit vanaf "date". Maandag telt
  // hier gewoon mee als werkdag: die kan een bufferdag zijn, ook al is
  // maandag zelf nooit een leverdag (dat wordt hierna apart afgedwongen).
  function addBusinessDays(date, days) {
    var d = new Date(date);
    var added = 0;
    while (added < days) {
      d = addDays(d, 1);
      if (!isWeekend(d)) added++;
    }
    return d;
  }

  function parseLocalDate(value) {
    if (!value) return null;
    var parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(function (n) { return isNaN(n); })) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function toISODate(date) {
    var y = date.getFullYear();
    var m = String(date.getMonth() + 1).padStart(2, '0');
    var d = String(date.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  function formatDutchDate(date) {
    return (
      WEEKDAYS_NL[date.getDay()] + ' ' + date.getDate() + ' ' + MONTHS_NL[date.getMonth()] + ' ' + date.getFullYear()
    );
  }

  // Eerst mogelijke leverdatum: minimaal MIN_LEAD_BUSINESS_DAYS werkdagen
  // vooruit vanaf "fromDate", en vervolgens doorschuiven tot de eerst-
  // volgende toegestane leverdag (geen weekend, geen maandag).
  //
  // Voorbeelden (besteldag -> eerst mogelijke leverdag, met 2 bufferdagen):
  //   maandag    -> donderdag  (dinsdag + woensdag zijn de bufferdagen)
  //   dinsdag    -> vrijdag
  //   woensdag   -> dinsdag (volgende week; donderdag + vrijdag zijn de
  //                 bufferdagen, maandag wordt overgeslagen)
  //   donderdag  -> dinsdag (volgende week; vrijdag + maandag zijn de
  //                 bufferdagen)
  //   vrijdag    -> woensdag (volgende week; maandag + dinsdag zijn de
  //                 bufferdagen)
  function computeEarliestDeliveryDate(fromDate) {
    var candidate = addBusinessDays(startOfDay(fromDate || new Date()), MIN_LEAD_BUSINESS_DAYS);
    while (!isDeliveryDayAllowed(candidate)) {
      candidate = addDays(candidate, 1);
    }
    return candidate;
  }

  // Retourneert { valid: true } of { valid: false, message }.
  function validateDate(value) {
    var picked = parseLocalDate(value);
    var earliest = computeEarliestDeliveryDate(new Date());

    if (!picked) {
      return { valid: false, message: 'Kies een geldige datum.' };
    }
    if (isWeekend(picked)) {
      return {
        valid: false,
        message: 'Levering in het weekend is niet mogelijk. Eerst mogelijke datum: ' + formatDutchDate(earliest) + '.',
      };
    }
    if (isMonday(picked)) {
      return {
        valid: false,
        message: 'Levering op maandag is niet mogelijk (geen palletlevering). Eerst mogelijke datum: ' + formatDutchDate(earliest) + '.',
      };
    }
    if (startOfDay(picked) < earliest) {
      return {
        valid: false,
        message: 'Kies een latere datum. Eerst mogelijke leverdatum: ' + formatDutchDate(earliest) + '.',
      };
    }
    return { valid: true };
  }

  function showError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideError(errorEl) {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function trimTrailingEmpty(lines) {
    var copy = lines.slice();
    while (copy.length && copy[copy.length - 1] === '') copy.pop();
    return copy;
  }

  // Vervangt (of verwijdert) alleen de palletlevering-regel in de cart note;
  // andere, door de klant zelf geschreven notities blijven volledig intact.
  function buildUpdatedNote(existingNote, newPalletLine) {
    var lines = trimTrailingEmpty((existingNote || '').split('\n'));
    var kept = trimTrailingEmpty(
      lines.filter(function (line) {
        return line.trim().indexOf(NOTE_PREFIX) !== 0;
      })
    );
    if (newPalletLine) kept.push(newPalletLine);
    return kept.join('\n');
  }

  function syncNoteTextarea(noteValue) {
    var textarea = document.getElementById(NOTE_TEXTAREA_ID);
    if (!textarea) return;
    if (document.activeElement === textarea) return; // niet overschrijven terwijl de klant typt
    textarea.value = noteValue;
  }

  function performSave() {
    var dateInput = document.getElementById(DATE_ID);
    var errorEl = document.getElementById(ERROR_ID);
    if (!dateInput) return Promise.resolve();

    var value = dateInput.value;

    // Nogmaals valideren vlak vóór het opslaan (defense-in-depth), ongeacht
    // welke weg hiernaartoe leidde.
    if (value) {
      var result = validateDate(value);
      if (!result.valid) {
        dateInput.value = '';
        value = '';
        showError(errorEl, result.message);
      }
    }

    var formattedLine = value ? NOTE_PREFIX + formatDutchDate(parseLocalDate(value)) : null;

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var updatedNote = buildUpdatedNote(cart.note || '', formattedLine);

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Gewenste leverdatum': value,
              // Opruimen van de "Voorkeur dagdeel"-keuze uit de vorige versie van deze functie.
              'Voorkeur dagdeel': '',
            },
            note: updatedNote,
          }),
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        syncNoteTextarea(cart.note || '');
        return cart;
      })
      .catch(function (error) {
        console.error('Palletlevering: kon de voorkeursdatum niet opslaan', error);
      });
  }

  function saveAttributes() {
    var request = saveQueue.then(performSave);
    // De keten blijft ook doorlopen als een eerder verzoek faalde.
    saveQueue = request.catch(function () {});

    saveInFlight = request;
    request.finally(function () {
      if (saveInFlight === request) saveInFlight = null;
    });

    return request;
  }

  function handleDateChange(event) {
    var dateInput = event.target;
    var errorEl = document.getElementById(ERROR_ID);
    hideError(errorEl);

    if (dateInput.value) {
      var result = validateDate(dateInput.value);
      if (!result.valid) {
        showError(errorEl, result.message);
        dateInput.value = '';
      }
    }

    saveAttributes();
  }

  function bindFields(root) {
    var scope = root || document;
    var dateInput = scope.querySelector('#' + DATE_ID);
    if (!dateInput || dateInput.dataset.palletBound === 'true') return;

    dateInput.dataset.palletBound = 'true';

    // Ondergrens meteen bijwerken naar de correcte, werkdag-bewuste datum
    // (het server-side "min"-attribuut is enkel een eenvoudige fallback).
    dateInput.min = toISODate(computeEarliestDeliveryDate(new Date()));

    // Een al opgeslagen datum die niet meer aan de regels voldoet (bv. een
    // oude zaterdag- of maandag-datum van vóór deze update) meteen opschonen.
    if (dateInput.value) {
      var result = validateDate(dateInput.value);
      if (!result.valid) {
        var errorEl = document.getElementById(ERROR_ID);
        showError(errorEl, result.message);
        dateInput.value = '';
        saveAttributes();
      }
    }

    dateInput.addEventListener('change', handleDateChange);
  }

  function interceptCheckoutSubmit() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement) || form.id !== FORM_ID) return;
        if (!saveInFlight) return;

        // Er loopt nog een opslag-verzoek (attribute + cart note): de
        // checkout-navigatie even laten wachten tot dat klaar is.
        event.preventDefault();
        event.stopImmediatePropagation();

        var submitter = event.submitter;
        saveInFlight.finally(function () {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
          } else {
            form.submit();
          }
        });
      },
      true
    );
  }

  function observeCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    var observer = new MutationObserver(function () {
      bindFields(cartDrawer);
    });
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  function init() {
    bindFields(document);
    observeCartDrawer();
    interceptCheckoutSubmit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
